/**
 * Pure helpers for the product catalogue UI (categories, sale units, unit
 * prices). No React and no I/O so the rules the quote line editor and the
 * product form share are unit-testable.
 */
import type { CatalogProduct, CatalogUnit } from './types/masterData.js';

const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹';
const ARABIC_DIGITS = '٠١٢٣٤٥٦٧٨٩';

/** Persian/Arabic digits and thousand separators → plain ASCII digits (what the API accepts). */
export function toAsciiDigits(text: string): string {
  return text
    .replace(/[۰-۹]/g, (d) => String(PERSIAN_DIGITS.indexOf(d)))
    .replace(/[٠-٩]/g, (d) => String(ARABIC_DIGITS.indexOf(d)))
    .replace(/[,٬،\s]/g, '')
    .replace(/[٫]/g, '.');
}

/** Rial price typed by a person → API string, '' when blank, null when not a whole number. */
export function parsePriceInput(text: string): string | '' | null {
  const ascii = toAsciiDigits(text);
  if (ascii === '') return '';
  return /^[0-9]{1,16}$/.test(ascii) ? ascii : null;
}

/** "12" or "1/1000" → rational parts, null when invalid (only positive whole parts). */
export function parseFactorInput(
  text: string,
): { readonly numerator: string; readonly denominator: string } | null {
  const ascii = toAsciiDigits(text);
  const match = /^([0-9]{1,12})(?:\/([0-9]{1,12}))?$/.exec(ascii);
  if (!match) return null;
  const numerator = match[1]!;
  const denominator = match[2] ?? '1';
  if (BigInt(numerator) <= 0n || BigInt(denominator) <= 0n) return null;
  return { numerator, denominator };
}

export function factorText(numerator: string, denominator: string): string {
  return denominator === '1' ? numerator : `${numerator}/${denominator}`;
}

/** Units that may be picked on a new document line: active ones, default first. */
export function sellableUnits(product: CatalogProduct | undefined): readonly CatalogUnit[] {
  if (!product) return [];
  const active = product.units.filter((u) => u.uomStatus === 'active');
  return [...active].sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
}

export function defaultUnit(product: CatalogProduct | undefined): CatalogUnit | undefined {
  return sellableUnits(product)[0];
}

const toPersianDigits = (text: string): string =>
  text.replace(/[0-9]/g, (d) => PERSIAN_DIGITS[Number(d)] ?? d);

/** «۱ کارتن = ۱۲ عدد» style hint; empty for the base unit. */
export function equivalenceHint(unit: CatalogUnit, base: CatalogUnit | undefined): string {
  if (unit.isBase || !base) return '';
  const factor = toPersianDigits(factorText(unit.factorNumerator, unit.factorDenominator));
  return `۱ ${unit.uomName} = ${factor} ${base.uomName}`;
}

export interface LinePatch {
  readonly uomCode: string;
  readonly unitPriceRial: string;
}

/** Prices of the chosen price list, keyed by {@link priceKey}. */
export type PriceListPrices = ReadonlyMap<string, string>;

export const priceKey = (productCode: string, uomCode: string): string =>
  `${productCode}\u0000${uomCode}`;

/**
 * The line fields that follow from picking a product: its default unit and that
 * unit's list price (blank when none is set, so the line starts empty, not 0).
 */
export function lineForProduct(
  product: CatalogProduct | undefined,
  prices?: PriceListPrices,
): LinePatch {
  const unit = defaultUnit(product);
  if (!unit || !product) return { uomCode: '', unitPriceRial: '' };
  // A chosen price list wins; products it does not price keep their own unit price.
  const listed = prices?.get(priceKey(product.code, unit.uomCode));
  return { uomCode: unit.uomCode, unitPriceRial: listed ?? unit.unitPriceRial ?? '' };
}

/** Switching the unit re-prices the line with that unit's list price. */
export function lineForUnit(
  product: CatalogProduct | undefined,
  uomCode: string,
  prices?: PriceListPrices,
): LinePatch {
  const unit = product?.units.find((u) => u.uomCode === uomCode);
  const listed = product ? prices?.get(priceKey(product.code, uomCode)) : undefined;
  return { uomCode, unitPriceRial: listed ?? unit?.unitPriceRial ?? '' };
}

/** Case/space-insensitive identity used to reuse an existing label instead of duplicating it. */
export function categoryKey(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLowerCase();
}
